import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, X, ChevronLeft, ChevronRight, Plus, Edit3, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  CatalogProductDetail,
  CatalogSearchHit,
  EnrichmentDetail,
  ProductEnrichment,
  getDistributorCatalogProduct,
  getEnrichment,
} from '../../services/distributorService';
import { attributesToFeatureRows } from '../productFeatureSpecs';

/**
 * Slide-out product detail pane — opens when the partner clicks a row in the
 * Catalogue browse table. Mounts on the right at 700px wide; backdrop is
 * semi-transparent so the list stays visible behind it (preserves
 * "where am I in the search results" mental model).
 *
 * Pagination chevrons in the header walk through the current results
 * array (`results[index]`) — keeps the partner's flow tight when they're
 * scanning for the right SKU. Esc + backdrop click close.
 *
 * Data:
 *  - Header / price / stock from the search hit.
 *  - Specs / "What's included" lazy-fetched from the enrichment for this
 *    SKU. SKU not yet in the partner's seed list → enrichment 404 → we
 *    render placeholder rows ("Not enriched yet").
 *
 * Honest deviations from the design comp:
 *  - "NSW & VIC warehouses": connector flattens to total stock; per-state
 *    breakdown deferred to a backend P&S extension.
 *  - "Open in Ingram" URL: Xvantage product-detail URL pattern unknown —
 *    falls back to an Ingram product search with the SKU as the query.
 *  - Compliance section: not in any data we capture. Omitted.
 */

interface ProductDetailPaneProps {
  results: CatalogSearchHit[];
  index: number;
  distributorId: string;
  distributorLabel: string;
  isInSeed: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onAdd: (hit: CatalogSearchHit) => void;
  /** Called with the SKU when the user clicks Edit — parent jumps to the Enrichment review tab. */
  onEdit: (hit: CatalogSearchHit) => void;
  /** Adding-in-flight indicator, drives the Add button spinner */
  adding: boolean;
  /**
   * Cached mode — used by the Synced Products surface where the SKU is already
   * on the partner's sync list. Skips the live distributor catalog round-
   * trip entirely (saves ~2 Ingram API calls per pane open) and tells the
   * server to serve price/stock from the per-partner distributor cache the
   * most recent sync wrote, instead of re-hitting Ingram /catalog/details +
   * /priceandavailability. The header "live from" copy is replaced with a
   * "last synced N min ago" subtitle so the partner can tell how stale the
   * numbers are.
   * Defaults to false (live mode) for the Catalogue browse flow where the
   * partner hasn't seen the SKU yet and a fresh upstream read is the point.
   */
  cached?: boolean;
  /**
   * Parent surfaces that already loaded enrichment summaries can pass a precise
   * yes/no so the pane does not probe /enrichments/{sku} for rows known not to
   * exist yet. That avoids noisy expected 404s in DevTools.
   */
  hasKnownEnrichment?: boolean;
}

export function ProductDetailPane({
  results,
  index,
  distributorId,
  distributorLabel,
  isInSeed,
  onClose,
  onPrev,
  onNext,
  onAdd,
  onEdit,
  adding,
  cached = false,
  hasKnownEnrichment,
}: ProductDetailPaneProps) {
  const hit = results[index];
  const [catalog, setCatalog] = useState<CatalogProductDetail | null>(null);
  const [enrichment, setEnrichment] = useState<EnrichmentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!hit) return;
    setLoadingDetail(true);
    setDetailError(null);
    setCatalog(null);
    setEnrichment(null);
    // Live mode (Catalogue browse): pulls authoritative price/stock/vendor/
    // description/category direct from Ingram alongside the enrichment
    // payload — both Ingram round-trips are unavoidable because the partner
    // is browsing SKUs they haven't approved yet and the side-by-side raw-vs-
    // enriched comparison demands a fresh upstream read.
    //
    // Cached mode (Synced Products): the SKU is already on the partner's sync
    // list, so we skip the live catalog round-trip altogether and ask
    // the server for the cached enrichment + cached price/stock (last sync's
    // values). Cuts per-click cost from ~4 Ingram hits to 0 and keeps the
    // pane responsive.
    try {
      const shouldLoadEnrichment = hasKnownEnrichment ?? isInSeed;
      if (cached) {
        if (shouldLoadEnrichment) {
          const enr = await getEnrichment(distributorId, hit.sku, { live: false }).catch(() => null);
          setEnrichment(enr);
        }
      } else {
        const [cat, enr] = await Promise.all([
          getDistributorCatalogProduct(distributorId, hit.sku),
          shouldLoadEnrichment
            ? getEnrichment(distributorId, hit.sku).catch(() => null)
            : Promise.resolve(null),
        ]);
        setCatalog(cat);
        setEnrichment(enr);
      }
    } catch (err: any) {
      setDetailError(err?.message ?? 'Failed to load product detail');
    } finally {
      setLoadingDetail(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, hit?.sku, cached, hasKnownEnrichment, isInSeed]);

  // Reload detail whenever the selected SKU changes (pagination).
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Esc + arrow-key navigation.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && index < results.length - 1) onNext();
      else if (e.key === 'ArrowLeft' && index > 0) onPrev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, index, results.length]);

  if (!hit) return null;

  const enrichmentPayload: ProductEnrichment | null = enrichment?.enrichment ?? null;
  const isEnriched = !!enrichmentPayload;
  const isLive = isEnriched && enrichment?.status === 'Active';
  // Source-of-truth chain for header fields:
  //  1. Live catalog fetch (always-on in browse mode, off in cached mode)
  //  2. Enrichment detail (priceAndStock + source) — server fills price/stock
  //     from the distributor cache when ?live=false, so cached mode still
  //     gets last-sync's numbers without an Ingram round-trip.
  //  3. The search hit's own data — keeps the pane from flickering "—"
  //     placeholders while the fetch is in flight.
  const livePrice = catalog?.priceAndStock?.price ?? enrichment?.priceAndStock?.price ?? hit.price;
  const liveStock = catalog?.priceAndStock?.stock ?? enrichment?.priceAndStock?.stock ?? hit.stock;
  const liveVendor = catalog?.source?.vendor ?? enrichment?.source?.vendor ?? hit.vendor;
  const liveVendorPN = catalog?.source?.vendorPartNumber ?? enrichment?.source?.vendorPartNumber ?? hit.vendorPartNumber;
  const liveDescription =
    catalog?.source?.displayDescription ??
    catalog?.source?.shortDescription ??
    enrichment?.source?.displayDescription ??
    enrichment?.source?.shortDescription ??
    hit.description;
  const liveCategory = catalog?.source?.category ?? enrichment?.source?.category ?? hit.category;
  // Tag what fed the price/stock card so the subtitle is honest about
  // whether the numbers are fresh from the distributor or cached from
  // the last sync.
  const priceSource: 'live' | 'cached' | 'unknown' =
    catalog?.priceAndStock?.price != null ? 'live'
    : enrichment?.priceAndStock?.price != null ? 'cached'
    : 'unknown';
  const priceFetchedAt = catalog?.priceAndStock?.fetchedAt ?? enrichment?.priceAndStock?.fetchedAt ?? null;
  // Google site-search lands reliably on Ingram's actual product pages
  // without us guessing the Xvantage URL pattern. Switch to a direct
  // Xvantage product URL once we confirm the live shape.
  const ingramUrl = `https://www.google.com/search?q=${encodeURIComponent(`${hit.sku} site:ingrammicro.com`)}`;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]" />
      <aside
        className="relative w-full max-w-[700px] bg-white shadow-2xl overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Product detail for ${hit.sku}`}
      >
        {/* Sticky header chrome — close, pagination */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-xs text-slate-600">
            <span>
              <span className="tabular-nums text-slate-900 font-medium">
                {index + 1}
              </span>
              {' of '}
              <span className="tabular-nums">{results.length.toLocaleString()}</span>
            </span>
            <button
              type="button"
              onClick={onPrev}
              disabled={index === 0}
              aria-label="Previous"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={index >= results.length - 1}
              aria-label="Next"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-5 space-y-6">
          {/* Header — vendor logo + SKU + PN + title + status pills */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <VendorTile vendor={liveVendor ?? null} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-3 text-xs text-slate-500">
                <span className="font-mono text-slate-700 font-medium">{hit.sku}</span>
                {liveVendorPN && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="font-mono text-slate-500">{liveVendorPN}</span>
                  </>
                )}
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mt-1.5 leading-snug">
                {prettifyTitle(
                  enrichmentPayload?.canonicalName ?? liveDescription ?? hit.sku,
                  liveVendor,
                )}
              </h2>
              <div className="mt-2.5 inline-flex items-center gap-1.5 flex-wrap">
                {isLive ? (
                  <Pill tone="green" dot>
                    Live
                  </Pill>
                ) : isEnriched && enrichment?.status === 'Draft' ? (
                  <Pill tone="amber" dot>
                    In review
                  </Pill>
                ) : (
                  <Pill tone="slate" dot>
                    Not enriched
                  </Pill>
                )}
                <Pill tone="blue">
                  <span className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-sm bg-blue-500 text-white text-[8px] font-bold mr-1">
                    {distributorBadge(distributorId)}
                  </span>
                  {distributorLabel}
                </Pill>
                {isEnriched && (
                  <Pill tone="orange">
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI-enriched
                  </Pill>
                )}
                {liveCategory?.l1 && (
                  <Pill tone="slate">{liveCategory.l1}</Pill>
                )}
              </div>
            </div>
          </div>

          {/* Price + stock cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Distributor price
              </div>
              <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">
                {livePrice != null ? formatMoney(livePrice) : '—'}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                excl. GST · {priceSource === 'cached'
                  ? `last synced from ${distributorLabel}`
                  : `live from ${distributorLabel}`}
                {priceFetchedAt && (
                  <> · {formatTimeAgo(priceFetchedAt)}</>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                In stock
              </div>
              <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums inline-flex items-center gap-2">
                {liveStock != null ? liveStock.toLocaleString() : '—'}
                {liveStock != null && (
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      liveStock > 10 ? 'bg-emerald-500' : liveStock > 0 ? 'bg-amber-500' : 'bg-rose-500'
                    }`}
                  />
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Across all warehouses
              </div>
            </div>
          </div>

          {/* Distributor description — always visible since the catalog
              fetch returns it whether or not the SKU is enriched. Keeps
              the pane useful for not-yet-enriched browse hits. */}
          {liveDescription && (
            <section>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">From {distributorLabel}</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                {liveDescription}
              </p>
            </section>
          )}

          {/* Detail body — enrichment-specific sections */}
          {loadingDetail ? (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading detail…
            </div>
          ) : detailError ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {detailError}
            </div>
          ) : isEnriched ? (
            <>
              <SpecsSection enrichment={enrichmentPayload!} />
              <WhatsIncludedSection enrichment={enrichmentPayload!} />
            </>
          ) : (
            <NotEnrichedHint isInSeed={isInSeed} />
          )}
        </div>

        {/* Sticky footer — actions */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onAdd(hit)}
            disabled={isInSeed || adding}
            className="inline-flex"
          >
            {adding ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : isInSeed ? (
              <span className="text-emerald-700 mr-1">✓</span>
            ) : (
              <Plus className="h-4 w-4 mr-1.5" />
            )}
            {isInSeed ? 'In sync list' : 'Add to sync list'}
          </Button>
          <div className="inline-flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onEdit(hit)}
              disabled={!isEnriched}
              title={isEnriched ? 'Open in Enrichment review' : isInSeed ? 'Waiting for enrichment before editing' : 'Add this SKU to enrich it first'}
              className="inline-flex"
            >
              <Edit3 className="h-4 w-4 mr-1.5" />
              Edit
            </Button>
            <a
              href={ingramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-3 py-2 rounded-md"
            >
              Open in {distributorLabel}
              <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </a>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ─── sections ────────────────────────────────────────────────

function SpecsSection({ enrichment }: { enrichment: ProductEnrichment }) {
  const rows = attributesToFeatureRows(enrichment.attributes ?? {}).map((row) => {
    const separator = row.text.indexOf(':');
    return separator > -1
      ? { label: row.text.slice(0, separator), value: row.text.slice(separator + 1).trim() }
      : { label: 'Feature', value: row.text };
  });

  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Specifications</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No specifications captured.</p>
      ) : (
        <dl className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.label} className="grid grid-cols-[140px_1fr] gap-3 text-sm">
              <dt className="text-slate-500">{r.label}</dt>
              <dd className="text-slate-900">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

const INCLUSION_KEYS: Array<{ key: string; label: string }> = [
  { key: 'inBox',   label: 'In box' },
  { key: 'software', label: 'Software' },
  { key: 'services', label: 'Services' },
];

function WhatsIncludedSection({ enrichment }: { enrichment: ProductEnrichment }) {
  const attrs = enrichment.attributes ?? {};
  const rows = INCLUSION_KEYS
    .map(({ key, label }) => ({ label, value: attrs[key] }))
    .filter((r) => r.value !== undefined && r.value !== null && r.value !== '');

  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5">
        <Sparkles className="h-4 w-4 text-orange-500" />
        What's included
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          Inclusions not parsed yet. The agent extracts these into{' '}
          <code className="text-[11px] bg-slate-100 px-1 rounded">attributes.inBox</code>,{' '}
          <code className="text-[11px] bg-slate-100 px-1 rounded">attributes.software</code>, and{' '}
          <code className="text-[11px] bg-slate-100 px-1 rounded">attributes.services</code>{' '}
          when the source description carries them.
        </p>
      ) : (
        <dl className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.label} className="grid grid-cols-[140px_1fr] gap-3 text-sm">
              <dt className="text-slate-500">{r.label}</dt>
              <dd className="text-slate-900">{String(r.value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function NotEnrichedHint({ isInSeed }: { isInSeed: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
      <p className="text-sm text-slate-700 font-medium">
        {isInSeed ? 'Awaiting enrichment' : 'Not enriched yet'}
      </p>
      <p className="text-xs text-slate-500 mt-1.5">
        {isInSeed ? (
          <>
            This SKU is already on the sync list. Once enrichment completes, the draft will appear
            in Enrichment review for approval.
          </>
        ) : (
          <>
            Click <span className="font-medium text-slate-700">Add to sync list</span> to track this
            SKU. The next sync will pull price, stock, and enrichment data for review.
          </>
        )}
      </p>
    </div>
  );
}

// ─── small components ────────────────────────────────────────────────

function Pill({
  tone,
  dot,
  children,
}: {
  tone: 'green' | 'amber' | 'blue' | 'orange' | 'slate';
  dot?: boolean;
  children: React.ReactNode;
}) {
  const map = {
    green:  'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber:  'bg-amber-50 text-amber-700 ring-amber-200',
    blue:   'bg-blue-50 text-blue-700 ring-blue-200',
    orange: 'bg-orange-50 text-orange-700 ring-orange-200',
    slate:  'bg-slate-50 text-slate-600 ring-slate-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${map[tone]}`}>
      {dot && <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70 mr-1.5" />}
      {children}
    </span>
  );
}

function VendorTile({ vendor }: { vendor: string | null }) {
  const label = (vendor ?? '??').slice(0, 2).toUpperCase();
  const map: Record<string, string> = {
    LE: 'bg-red-500',
    HP: 'bg-blue-500',
    DE: 'bg-cyan-600',
    CI: 'bg-blue-700',
    FO: 'bg-rose-600',
    MI: 'bg-emerald-600',
    AP: 'bg-slate-700',
    AR: 'bg-orange-500',
    LO: 'bg-violet-500',
  };
  const colour = map[label] ?? 'bg-slate-500';
  return (
    <div className={`h-16 w-16 rounded-lg flex items-center justify-center text-white font-bold text-lg ${colour}`}>
      {label}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────

function formatTimeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function prettifyTitle(text: string, vendor: string | null): string {
  if (!text) return '—';
  if (vendor) {
    const re = new RegExp(`^${vendor}\\s+`, 'i');
    return text.replace(re, '');
  }
  return text;
}

function distributorBadge(distributorId: string): string {
  const map: Record<string, string> = {
    ingram: 'IM',
    dickerdata: 'DD',
    telstra: 'TE',
  };
  return map[distributorId] ?? '??';
}
